import styled, { keyframes } from 'styled-components';

// Animations
export const float = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-14px) rotate(2deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

export const glow = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 1; }
  100% { opacity: 0.5; }
`;

export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const shine = keyframes`
  0% { transform: translateX(-120%); }
  100% { transform: translateX(220%); }
`;

export const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.04); }
`;

// Layout wrappers
export const PageWrap = styled.div`
  background-color: #ffffff;
  min-height: 100vh;
  width: 100%;
`;

export const Hero = styled.section`
  position: relative;
  overflow: hidden;
  min-height: 60vh;
  display: flex;
  align-items: center;
  background: #ffffff; /* Clean white background */
  padding-top: 80px; /* Offset for fixed header */
  
  @media (max-width: 768px) {
    padding-top: 60px;
    min-height: auto;
  }
`;

export const HeroBgImage = styled.div`
  position: absolute;
  inset: 0;
  /* New background image: a driving school or road-related setting */
  background-image: url('https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&q=80&w=2000');
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  opacity: 0.15; /* Subtle image background */
  z-index: 0;
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 20% 50%, rgba(255,255,255,0.9), transparent 80%);
    z-index: 1;
  }
`;


export const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 4rem;
  position: relative;
  z-index: 2;
  align-items: center;
  
  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 0; /* Reduced gap since image has margin-bottom */
  }
`;

export const HeroTextSide = styled.div`
  padding: 4rem 0 4rem 4rem;
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 960px) {
    padding: 0.5rem 1.5rem 2rem;
    align-items: flex-start;
    text-align: left;
    order: 2; /* Explicit order for clarity */

    p {
        text-align: left !important;
    }

    div {
        text-align: left !important;
    }
  }

  @media (max-width: 600px) {
    padding: 1.5rem 1rem;
    
    h1 {
        font-size: clamp(1.2rem, 7vw, 1.8rem) !important;
        line-height: 1.1 !important;
        margin-bottom: 0.75rem !important;
    }
  }
  
  .reveal {
    opacity: 0;
    transform: translateY(30px);
    will-change: transform, opacity;
    animation: ${fadeUp} .8s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
  }
  .delay-0 { animation-delay: .1s; }
  .delay-1 { animation-delay: .25s; }
  .delay-2 { animation-delay: .4s; }
  .delay-3 { animation-delay: .55s; }
`;

export const PricingBadge = styled.div`
  display: inline-flex;
  background: rgba(34, 197, 94, 0.1);
  color: #15803d;
  padding: 0.35rem 1rem;
  border-radius: 99px;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 2rem;
  border: 1px solid rgba(34, 197, 94, 0.2);

  @media (max-width: 600px) {
    font-size: 0.85rem;
    padding: 0.25rem 0.75rem;
    margin-bottom: 1.5rem;
  }
`;

export const HeroVisualSide = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  perspective: 1200px;
  order: 1;

  @media (max-width: 960px) {
    order: 1;
    margin-bottom: 2rem;
  }
  
  &::before {
    content: '';
    position: absolute;
    width: 140%;
    height: 140%;
    background: radial-gradient(circle, rgba(37, 99, 235, 0.08) 0%, transparent 70%);
    z-index: 0;
    animation: ${glow} 8s ease-in-out infinite;
  }

  .reveal {
    opacity: 0;
    transform: translateY(30px);
    will-change: transform, opacity;
    animation: ${fadeUp} .8s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
  }
  .delay-1 { animation-delay: .25s; }
`;

export const HeroIntroCta = styled.button`
  display: flex;
  align-items: center;
  gap: 1rem;
  background: var(--primary-orange);
  color: #fff;
  padding: 1.25rem 2.5rem;
  border-radius: 100px;
  font-size: 1.1rem;
  font-weight: 800;
  border: none;
  cursor: pointer;
  box-shadow: 0 15px 35px rgba(255, 87, 34, 0.25);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0; left: 0; width: 100%; height: 100%;
    background: linear-gradient(120deg, transparent, rgba(255,255,255,0.3), transparent);
    animation: ${shine} 4s infinite linear;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 45px rgba(255, 87, 34, 0.35);
    background: #e64a19;
  }

  &:active { transform: translateY(-2px); }

  .arrow { transition: transform 0.3s ease; }
  &:hover .arrow { transform: translateX(5px); }

  @media (max-width: 600px) {
    padding: 1rem 2rem;
    font-size: 1rem;
    width: 100%;
    justify-content: center;
  }
`;

export const HeroBadge = styled.div`
  position: absolute;
  background: #fff;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  z-index: 2;
  border: 1px solid rgba(0,0,0,0.05);

  span {
    font-weight: 700;
    font-size: 0.9rem;
    color: #111;
  }

  svg {
    color: var(--primary-orange);
  }

  &.floating-1 {
    top: 15%;
    right: -5%;
    animation: ${float} 6s ease-in-out infinite;
  }
  &.floating-2 {
    bottom: 20%;
    left: -5%;
    animation: ${float} 6s ease-in-out infinite reverse;
  }
  &.floating-3 {
    top: 50%;
    left: -10%;
    animation: ${float} 7s ease-in-out infinite;
  }

  @media (max-width: 960px) {
    position: absolute;
    top: auto;
    bottom: auto;
    left: auto;
    right: auto;
    margin: 0;
    animation: none !important;
    flex-direction: row; 
    justify-content: center;
    width: fit-content;
    box-shadow: 0 5px 15px rgba(0,0,0,0.2);

    &.floating-1 {
        top: 0%;
        right: 0%;
    }
    &.floating-2 {
        bottom: 10%;
        left: 0%;
    }
    &.floating-3 {
        top: 0%;
        left: 0%;
    }
  }

  @media (max-width: 600px) {
     padding: 0.6rem 1rem;
     span {
       font-size: 0.8rem;
     }
  }
`;

export const FeatureTag = styled.div`
  display: inline-block;
  padding: 0.5rem 1rem;
  background: rgba(37, 99, 235, 0.1);
  color: var(--primary-blue);
  border-radius: 99px;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1rem;
`;

export const BenefitList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 600px) {
    gap: 0.5rem;
    margin-bottom: 1.5rem !important;
  }
`;

export const BenefitItem = styled.div`
  display: flex;
  gap: 1.25rem;
  align-items: flex-start;

  @media (max-width: 960px) {
    flex-direction: row;
    align-items: flex-start;
    text-align: left;
    gap: 1rem;
    margin-bottom: 0.5rem;
  }

  @media (max-width: 600px) {
    gap: 0.75rem;
    margin-bottom: 0;

    .content {
        text-align: left !important;
    }
  }

  .check {
    width: 24px;
    height: 24px;
    background: #22c55e;
    color: #fff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 0.7rem;
    font-weight: 900;
    margin-top: 2px;
    box-shadow: 0 4px 10px rgba(34, 197, 94, 0.2);

    @media (max-width: 600px) {
        width: 20px;
        height: 20px;
        font-size: 0.6rem;
        margin-top: 1px;
    }
  }

  .content {
    h4 {
      margin: 0 0 0.25rem;
      font-size: 1.1rem;
      font-weight: 800;
      color: #111;

      @media (max-width: 600px) {
        font-size: 1rem;
        margin-bottom: 0;
      }
    }
    p {
      margin: 0;
      color: #64748b;
      line-height: 1.6;

      @media (max-width: 600px) {
        font-size: 0.75rem;
        line-height: 1.2;
      }
    }
  }
`;

export const HeroTopText = styled.h2`
  font-family: var(--font-heading);
  font-size: clamp(1.2rem, 3vw, 2.5rem);
  font-weight: 500;
  color: #111;
  margin-bottom: 0.5rem;
  line-height: 1.2;

  @media (max-width: 600px) {
    font-size: 1.4rem;
    font-weight: 600;
  }
`;

export const HeroMiddleText = styled.h1`
  font-family: var(--font-heading);
  font-size: var(--font-size-h1);
  font-weight: 900;
  color: #111;
  margin: 0 0 1rem 0;
  line-height: 1.0;

  @media (max-width: 600px) {
    font-size: var(--font-size-h2);
    line-height: 1.1;
    margin: 0.5rem 0 1rem 0;
  }
`;

export const TrustSection = styled.section`
  padding: 60px 0;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  
  .trust-grid {
    display: flex;
    justify-content: space-around;
    gap: 3rem;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 2.5rem;
      align-items: center;
    }
  }

  .trust-item {
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    strong {
      font-size: 2.5rem;
      font-weight: 900;
      color: #111;
      line-height: 1;
    }

    span {
      color: #64748b;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      font-size: 0.85rem;
    }
  }

  .reveal {
    opacity: 0;
    transform: translateY(20px);
    animation: ${fadeUp} 0.6s ease forwards;
  }
  .delay-0 { animation-delay: 0.1s; }
  .delay-1 { animation-delay: 0.2s; }
  .delay-2 { animation-delay: 0.3s; }
`;

export const HowSection = styled.section`
  padding: 120px 0;
  background: #ffffff;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

export const HowIntroHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 5rem;
  gap: 4rem;

  @media (max-width: 960px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 2rem;
  }
`;

export const SectionTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 1rem;
  background: rgba(37, 99, 235, 0.06);
  color: var(--primary-blue);
  border-radius: 99px;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 1rem;
  border: 1px solid rgba(37, 99, 235, 0.1);
`;

export const PrimaryCta = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background: #2563eb;
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 25px rgba(37, 99, 235, 0.2);

  &:hover {
    background: #1d4ed8;
    transform: translateY(-2px);
    box-shadow: 0 15px 30px rgba(37, 99, 235, 0.3);
  }

  @media (max-width: 600px) {
    width: 100%;
    justify-content: center;
  }
`;

export const AuthModalContent = styled.div`
    padding: 1.5rem 2rem 2.5rem 2rem;
`;

export const AuthModalHeader = styled.div`
    text-align: center;
    margin-bottom: 2rem;
    h2 { margin: 0; font-size: 1.75rem; font-weight: 800; color: #111; }
    p { margin: 0.5rem 0 0; color: #666; font-size: 1rem; }
`;

export const RegisterModalLayout = styled.div`
    display: flex;
    min-height: 600px;
    @media (max-width: 850px) {
        flex-direction: column;
    }
`;

export const RegisterModalVisual = styled.div`
    flex: 1;
    background: var(--color-peach);
    padding: 2.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    text-align: center;

    h2 { font-size: 2rem; font-weight: 800; color: #111; margin: 0 0 4rem 0; max-width: 300px; }

    .visual-box {
        position: relative;
        width: 100%;
        max-width: 340px;
        aspect-ratio: 1;
        background: #fff;
        border-radius: 30px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        box-shadow: 0 20px 50px rgba(0,0,0,0.05);
        padding: 2rem;
    }

    .modal-logo {
        height: 40px;
        margin-bottom: 2rem;
    }

    .device-frame {
        width: 240px;
        height: 160px;
        background: #333;
        border-radius: 12px;
        padding: 6px;
        box-shadow: 0 10px 30px rgba(0,0,0,0.1);
    }

    .screen-content {
        width: 100%;
        height: 100%;
        background: #f9f9f9;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 3rem;
    }

    @media (max-width: 850px) {
        display: none;
    }
`;

export const MobileCloseButton = styled.button`
    display: none;
    position: absolute;
    top: 1rem;
    right: 1rem;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #f1f5f9;
    border: none;
    color: #475569;
    font-size: 1.25rem;
    font-weight: 700;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    transition: all 0.2s ease;

    &:hover {
        background: #e2e8f0;
        color: #1e293b;
    }

    @media (max-width: 850px) {
        display: flex;
    }
`;

export const RegisterModalFormSide = styled.div`
    flex: 1.2;
    padding: 2.5rem;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
    
    @media (max-width: 600px) {
        padding: 1rem 1.25rem;
    }
`;

export const HeroBottomText = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 87, 34, 0.08);
  color: var(--primary-orange, #ff5722);
  padding: 0.6rem 1.2rem;
  border-radius: 100px;
  font-weight: 700;
  font-size: 0.95rem;
  margin-top: 0.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(255, 87, 34, 0.15);
  letter-spacing: 0.02em;

  &::before {
    content: '★';
    font-size: 0.8rem;
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;
    padding: 0.5rem 1rem;
  }
`;

export const FinalCta = styled.section`
  text-align: center;
  padding: 120px 0;
  position: relative;
  overflow: hidden;
  background: #0f172a;
  color: #fff;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url('https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&q=80&w=2000');
    background-size: cover;
    background-position: center;
    opacity: 0.2;
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, rgba(15, 23, 42, 0.9), rgba(15, 23, 42, 0.7));
    z-index: 1;
  }

  .container-relative {
    position: relative;
    z-index: 2;
  }

  .title { 
    font-family: var(--font-heading);
    font-size: var(--font-size-h1);
    font-weight: 900;
    color: #fff; 
    margin-bottom: 1.5rem;
    line-height: 1.1;

    @media (max-width: 600px) {
        font-size: 2rem;
    }
  }

  .desc {
    color: rgba(255,255,255,0.7);
    font-size: 1.25rem;
    max-width: 600px;
    margin: 0 auto 3rem;
    line-height: 1.6;

    @media (max-width: 600px) {
        font-size: 1.1rem;
    }
  }

  .reveal {
    opacity: 0;
    transform: translateY(30px);
    animation: ${fadeUp} .8s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
  }
  .delay-1 { animation-delay: .1s; }
  .delay-2 { animation-delay: .2s; }
  .delay-3 { animation-delay: .3s; }
`;

export const StudentSection = styled.section`
  padding: 100px 0;
  background: #ffffff;
  position: relative;

  @media (max-width: 768px) {
    padding: 60px 0;
  }

  .student-grid {
    display: grid;
    grid-template-columns: 1.2fr 1fr;
    gap: 5rem;
    align-items: center;

    @media (max-width: 960px) {
      grid-template-columns: 1fr;
      gap: 3rem;
      text-align: center;
    }
  }

  .reveal {
    opacity: 0;
    transform: translateY(20px);
    animation: ${fadeUp} 0.7s ease-out forwards;
  }
  .delay-0 { animation-delay: 0.1s; }
  .delay-2 { animation-delay: 0.2s; }
  .delay-3 { animation-delay: 0.3s; }
  .delay-4 { animation-delay: 0.4s; }
`;

export const StudentTextSide = styled.div`
  text-align: left;

  @media (max-width: 960px) {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .desc {
    font-size: 1.25rem;
    line-height: 1.7;
    color: #475569;
    margin-bottom: 2rem;
    max-width: 600px;

    @media (max-width: 600px) {
        font-size: 1.1rem;
    }
  }
`;

export const ReferralBox = styled.div`
  background: #f8fafc;
  padding: 2.5rem;
  border-radius: 24px;
  border: 1px solid #e2e8f0;
  position: relative;
  z-index: 2;
  
  h4 {
    color: #0f172a;
    font-size: 1.25rem;
    font-weight: 700;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    
    @media (max-width: 960px) {
      justify-content: center;
    }
  }
  
  p {
    color: #64748b;
    line-height: 1.6;
    font-size: 1rem;
  }
`;

export const HowIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 3.5rem;
  text-align: left;
`;

export const HowTitle = styled.h2`
  font-size: var(--font-size-h5);
  font-weight: 800;
  color: #0f172a;
  line-height: 1.1;
  margin: 0;
`;

export const HowGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const HowCard = styled.div`
  background: #0f0f10;
  color: #fff;
  border-radius: 18px;
  padding: 0;
  box-shadow: 0 10px 25px rgba(0,0,0,0.12);
  transition: transform .3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow .3s ease, border-color .3s ease;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.08);
  display: flex;
  flex-direction: column;

  .card-image {
    position: relative;
    height: 180px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform .5s ease;
    }

    .overlay {
      position: absolute;
      inset: 0;
      background: linear-gradient(to bottom, transparent 0%, rgba(15, 15, 16, 0.6) 100%);
    }
  }

  .card-content {
    padding: 1.25rem 1.5rem 1.75rem;
    position: relative;
    z-index: 2;
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 15px 35px rgba(0,0,0,0.3);
    border-color: rgba(255,255,255,0.15);
    
    .card-image img {
      transform: scale(1.05);
    }
  }
`;

export const HowIcon = styled.div`
  position: absolute;
  bottom: 1.25rem;
  left: 1.5rem;
  width: 44px; height: 44px;
  display: grid; place-items: center;
  border-radius: 10px;
  color: #fff;
  background: var(--primary-orange);
  box-shadow: 0 6px 15px rgba(230, 115, 80, 0.3);
  z-index: 3;
  transition: transform .25s ease;
  
  svg {
    font-size: 24px !important;
  }

  ${HowCard}:hover & { transform: translateY(-3px) scale(1.05); }
`;

export const HowCardTitle = styled.h3`
  font-size: var(--font-size-h6);
  font-weight: 800;
  color: #fff;
  margin: 0 0 .5rem;
  text-align: left;
`;

export const HowCardDesc = styled.p`
  font-size: var(--font-size-small);
  line-height: 1.5;
  color: rgba(255,255,255,0.7);
  margin: 0;
  text-align: left;
`;

export const HeroVisualImg = styled.img`
  width: 100%;
  max-width: 600px;
  height: auto;
  border-radius: 24px;
  box-shadow: 0 30px 60px rgba(0,0,0,0.12);
  transform: perspective(1000px) rotateY(-10deg) rotateX(5deg);
  border: 1px solid rgba(0,0,0,0.05);
`;

export const RegisterContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  justify-content: flex-start;
  width: 100%;

  h2 {
    margin-bottom: 3rem;
  }
`;

export const StudentRegisterFormSide = styled.div`
  flex: 1.2;
  padding: 2.5rem;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  
  @media (max-width: 600px) {
    padding: 1rem 1.25rem;
  }
`;

export const BenefitListContainer = styled.div`
  text-align: left;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  width: 100%;
`;

